import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'store/hooks';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { CreateArgumentButton } from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/ArgumentList/CreateArgumentButton';
import Loading from 'components/Loading';
import ModalForm from 'components/Form/ModalForm';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';

import ArgumentCard from './ArgumentCard';
import { createArgument, useGetArguments } from '../hooks/arguments';
import { CurrentBudgetProposalContext } from 'routes/BudgetModule/components/BudgetProposal/Resource/contexts/CurrentBudgetProposalContext';
import SearchBar from 'ui/SearchBar/SearchBar';
import { ArgumentT } from 'routes/BudgetModule/config/types';

const StyArgumentWrapper = styled(Box, {
    name: 'StyArgumentWrapper',
})({
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
});

const ArgumentList: React.FC<{
    resourceName: string;
}> = ({ resourceName }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [argumentNameSearch, setArgumentNameSearch] = useState<string>('');

    const { budgetProposalId = '', resourceSoftwareId = '' } = useParams();
    const budgetProposal = useContext(CurrentBudgetProposalContext);

    const trackingMetadata = {
        budgetProposal: budgetProposal.name,
        phase: t(`budgetProposal.${budgetProposal.phase}`),
        service: resourceName,
    };

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [newArgumentTitle, setNewArgumentTitle] = useState<string>('');

    const {
        data: budgetArguments,
        isLoading,
        isError,
    } = useGetArguments({
        budgetProposalId,
        resourceSoftwareId,
    });
    const filteredArguments = budgetArguments
        ?.filter((arg: ArgumentT) =>
            arg.title.toLowerCase().includes(argumentNameSearch.toLowerCase()),
        )
        .sort((a: ArgumentT, b: ArgumentT) => a.title.localeCompare(b.title));

    const createArgumentMutation = useMutation({
        mutationFn: () =>
            createArgument({
                budgetProposalId,
                resourceSoftwareId,
                title: newArgumentTitle,
            }),

        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ['arguments', resourceSoftwareId],
            });
            dispatch.vision.notifyUser({
                message:
                    data.content.message ||
                    t('budgetProposal.newArgumentCreated'),
                snackType: 'success',
            });
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.allArguments.create,
                trackingMetadata,
            );
        },

        meta: {
            useGlobalOnError: true,
        },
    });

    if (isLoading || createArgumentMutation.isPending) return <Loading />;

    if (isError || createArgumentMutation.isError)
        return (
            <Typography role="alert">{t('asyncComponent.error')}</Typography>
        );

    const onClose = () => setIsModalOpen(false);
    const onSubmit = (data: { title: string }) => {
        setNewArgumentTitle(data.title);
        createArgumentMutation.mutate();
        onClose();
    };

    return (
        <Box sx={{ margin: '2rem' }}>
            <CreateArgumentButton onClick={() => setIsModalOpen(true)} />
            <SearchBar
                handleChange={setArgumentNameSearch}
                width="18rem"
                placeholder={t('budgetProposal.findArgumentaire')}
                sx={{
                    width: '20rem',
                    marginTop: '1rem',
                    border: '1px solid lightgray',
                }}
            />
            <ModalForm
                show={isModalOpen}
                closeModal={onClose}
                submit={onSubmit}
                submitText={t('common.save')}
                title={t('budgetProposal.createEmptyArgument')}
                fields={[
                    {
                        key: 'title',
                        name: 'title',
                        label: t('common.title'),
                        type: 'text',
                        value: t('budgetProposal.newArgument'),
                        validators: ['required'],
                    },
                ]}
            />
            <StyArgumentWrapper>
                {isEmpty(budgetArguments) && (
                    <Box sx={{ margin: '2rem 0' }}>
                        <Typography>
                            {t('budgetProposal.noArgument')}
                        </Typography>
                    </Box>
                )}
                {!isEmpty(budgetArguments) && isEmpty(filteredArguments) && (
                    <Typography sx={{ display: 'block', my: '2rem' }}>
                        {t('budgetProposal.noArgumentFound')}
                    </Typography>
                )}
                {!isEmpty(filteredArguments) &&
                    filteredArguments?.map((budgetArg) => (
                        <ArgumentCard
                            key={budgetArg.id}
                            argument={budgetArg}
                            resourceSoftwareId={resourceSoftwareId}
                        />
                    ))}
            </StyArgumentWrapper>
        </Box>
    );
};

export default ArgumentList;
